import React, { useState } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../../primitives/Button/Button';
import { SearchInput } from '../../primitives/Input/Input';
import { Badge, Tag } from '../../primitives/Badge/Badge';

/**
 * FilterSection Component
 *
 * Reusable filter sidebar sections with various filter types:
 * checkboxes, radio buttons, search, tags, etc.
 */

// Icons
const ChevronDownIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const SearchIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

/**
 * Collapsible Filter Section
 */
export function FilterSection({
  title,
  children,
  defaultOpen = true,
  badge,
  className,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={cn('border-b border-[var(--color-border-subtle)] last:border-b-0', className)} {...props}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'w-full flex items-center justify-between',
          'py-3 px-1',
          'text-left',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-primary-500)]'
        )}
      >
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">
            {title}
          </span>
          {badge && (
            <Badge variant="primary" size="xs">
              {badge}
            </Badge>
          )}
        </span>
        <ChevronDownIcon
          className={cn(
            'w-4 h-4 text-[var(--color-text-muted)]',
            'transition-transform duration-[var(--duration-fast)]',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      <div
        className={cn(
          'overflow-hidden',
          'transition-all duration-[var(--duration-normal)]',
          isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'
        )}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Checkbox Filter Option
 */
export function FilterCheckbox({
  label,
  checked,
  onChange,
  count,
  disabled,
  className,
  ...props
}) {
  return (
    <label
      className={cn(
        'flex items-center gap-3 py-1.5',
        'cursor-pointer',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...props}
    >
      <div
        className={cn(
          'relative flex-shrink-0',
          'w-4 h-4',
          'rounded',
          'border',
          'transition-all duration-[var(--duration-fast)]',
          checked
            ? 'bg-[var(--color-primary-500)] border-[var(--color-primary-500)]'
            : 'bg-white border-[var(--color-border-strong)] hover:border-[var(--color-primary-400)]'
        )}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="sr-only"
        />
        {checked && (
          <CheckIcon className="absolute inset-0 w-4 h-4 text-white p-0.5" />
        )}
      </div>
      <span
        className={cn(
          'flex-1 text-sm',
          checked ? 'text-[var(--color-text-primary)] font-medium' : 'text-[var(--color-text-secondary)]'
        )}
      >
        {label}
      </span>
      {count !== undefined && (
        <span className="text-xs text-[var(--color-text-muted)]">
          {count.toLocaleString()}
        </span>
      )}
    </label>
  );
}

/**
 * Checkbox Filter Group
 */
export function FilterCheckboxGroup({
  options,
  selected = [],
  onChange,
  searchable,
  searchPlaceholder = 'Search...',
  maxVisible = 5,
  className,
  ...props
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);

  const filteredOptions = searchable && searchQuery
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  const visibleOptions = showAll ? filteredOptions : filteredOptions.slice(0, maxVisible);
  const hasMore = filteredOptions.length > maxVisible;

  const handleToggle = (value) => {
    const newSelected = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  return (
    <div className={cn('space-y-2', className)} {...props}>
      {searchable && (
        <div className="relative mb-3">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-text-muted)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className={cn(
              'w-full pl-9 pr-3 py-2',
              'text-sm',
              'bg-[var(--color-neutral-50)]',
              'border border-transparent',
              'rounded-[var(--radius-md)]',
              'placeholder:text-[var(--color-text-muted)]',
              'focus:outline-none focus:border-[var(--color-primary-500)] focus:bg-white'
            )}
          />
        </div>
      )}

      <div className="space-y-0.5">
        {visibleOptions.map((option) => (
          <FilterCheckbox
            key={option.value}
            label={option.label}
            checked={selected.includes(option.value)}
            onChange={() => handleToggle(option.value)}
            count={option.count}
            disabled={option.disabled}
          />
        ))}
      </div>

      {hasMore && !searchQuery && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className={cn(
            'text-sm font-medium',
            'text-[var(--color-primary-500)]',
            'hover:text-[var(--color-primary-600)]',
            'transition-colors duration-[var(--duration-fast)]'
          )}
        >
          {showAll ? 'Show less' : `Show ${filteredOptions.length - maxVisible} more`}
        </button>
      )}
    </div>
  );
}

/**
 * Tag-style filter (multi-select chips)
 */
export function FilterTagGroup({
  options,
  selected = [],
  onChange,
  className,
  ...props
}) {
  const handleToggle = (value) => {
    const newSelected = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(newSelected);
  };

  return (
    <div className={cn('flex flex-wrap gap-2', className)} {...props}>
      {options.map((option) => (
        <Tag
          key={option.value}
          selected={selected.includes(option.value)}
          onClick={() => handleToggle(option.value)}
          icon={option.icon}
        >
          {option.label}
        </Tag>
      ))}
    </div>
  );
}

/**
 * Range slider filter (for things like ratings, price)
 */
export function FilterRange({
  label,
  min = 0,
  max = 100,
  value = [min, max],
  onChange,
  step = 1,
  formatValue = (v) => v,
  className,
  ...props
}) {
  const [localValue, setLocalValue] = useState(value);

  const handleMinChange = (e) => {
    const newMin = Math.min(Number(e.target.value), localValue[1] - step);
    setLocalValue([newMin, localValue[1]]);
    onChange([newMin, localValue[1]]);
  };

  const handleMaxChange = (e) => {
    const newMax = Math.max(Number(e.target.value), localValue[0] + step);
    setLocalValue([localValue[0], newMax]);
    onChange([localValue[0], newMax]);
  };

  return (
    <div className={cn('space-y-3', className)} {...props}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-[var(--color-text-secondary)]">{label}</span>
        <span className="text-sm font-medium text-[var(--color-text-primary)]">
          {formatValue(localValue[0])} - {formatValue(localValue[1])}
        </span>
      </div>

      <div className="relative h-2">
        {/* Track */}
        <div className="absolute inset-0 bg-[var(--color-neutral-200)] rounded-full" />

        {/* Active track */}
        <div
          className="absolute h-full bg-[var(--color-primary-500)] rounded-full"
          style={{
            left: `${((localValue[0] - min) / (max - min)) * 100}%`,
            right: `${100 - ((localValue[1] - min) / (max - min)) * 100}%`,
          }}
        />

        {/* Min slider */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue[0]}
          onChange={handleMinChange}
          className={cn(
            'absolute inset-0 w-full',
            'appearance-none bg-transparent',
            'pointer-events-none',
            '[&::-webkit-slider-thumb]:pointer-events-auto',
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4',
            '[&::-webkit-slider-thumb]:bg-white',
            '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[var(--color-primary-500)]',
            '[&::-webkit-slider-thumb]:rounded-full',
            '[&::-webkit-slider-thumb]:shadow-[var(--shadow-sm)]',
            '[&::-webkit-slider-thumb]:cursor-pointer'
          )}
        />

        {/* Max slider */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue[1]}
          onChange={handleMaxChange}
          className={cn(
            'absolute inset-0 w-full',
            'appearance-none bg-transparent',
            'pointer-events-none',
            '[&::-webkit-slider-thumb]:pointer-events-auto',
            '[&::-webkit-slider-thumb]:appearance-none',
            '[&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4',
            '[&::-webkit-slider-thumb]:bg-white',
            '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-[var(--color-primary-500)]',
            '[&::-webkit-slider-thumb]:rounded-full',
            '[&::-webkit-slider-thumb]:shadow-[var(--shadow-sm)]',
            '[&::-webkit-slider-thumb]:cursor-pointer'
          )}
        />
      </div>
    </div>
  );
}

/**
 * Active Filters Display
 */
export function ActiveFilters({
  filters = [],
  onRemove,
  onClearAll,
  className,
  ...props
}) {
  if (filters.length === 0) return null;

  return (
    <div
      className={cn(
        'flex items-center flex-wrap gap-2',
        'pb-4 mb-4',
        'border-b border-[var(--color-border-subtle)]',
        className
      )}
      {...props}
    >
      <span className="text-xs font-medium text-[var(--color-text-tertiary)] uppercase tracking-wider">
        Active:
      </span>
      {filters.map((filter, index) => (
        <Badge
          key={index}
          variant="primary"
          size="sm"
          removable
          onRemove={() => onRemove(filter)}
        >
          {filter.label}
        </Badge>
      ))}
      {filters.length > 1 && (
        <button
          type="button"
          onClick={onClearAll}
          className={cn(
            'text-xs font-medium',
            'text-[var(--color-error-500)]',
            'hover:text-[var(--color-error-600)]',
            'transition-colors duration-[var(--duration-fast)]'
          )}
        >
          Clear all
        </button>
      )}
    </div>
  );
}

/**
 * Filter Sidebar Container
 */
export function FilterSidebar({
  children,
  onReset,
  className,
  ...props
}) {
  return (
    <div className={cn('space-y-1', className)} {...props}>
      {children}

      {onReset && (
        <div className="pt-4 border-t border-[var(--color-border-subtle)]">
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={onReset}
          >
            Reset all filters
          </Button>
        </div>
      )}
    </div>
  );
}

export default FilterSection;
